import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type KeyboardEvent } from "react";
import { Link, useParams } from "react-router";
import { useIdempotentAction } from "../../shared/api/idempotency";
import { isApiProblem, problemMessage } from "../../shared/api/problem";
import { useProjectMembers, useUserDirectory } from "../../shared/api/queries";
import type { RetrospectiveEntry, RetrospectiveListKind, SprintRetrospective } from "../../shared/api/types";
import { useAnnounce } from "../../shared/components/announcer-context";
import { Dialog } from "../../shared/components/Dialog";
import { LoadError } from "../../shared/components/LoadError";
import { PageNotAvailable } from "../../shared/components/NotAvailable";
import { PersonAvatar } from "../../shared/components/PersonAvatar";
import { StatusBadge } from "../../shared/components/StatusBadge";
import { formatDate, formatDateTime } from "../../shared/lib/format";
import { userLabel } from "../../shared/lib/personLabel";
import { langFor } from "../../shared/lib/thai";
import { useTitle } from "../../shared/lib/useTitle";
import { useSession } from "../../shared/session/session-context";
import { textQualityErrors } from "../items/textQuality";
import { addEntry, createActionItem, editEntry, removeEntry, retrospectiveKey, useRetrospective } from "./retrospectiveApi";
import { LISTS, defaultDueDate, entriesOf, factRows, mayOwnAction, retrospectiveAsText } from "./retrospectiveModel";
import { retrospectiveText as t } from "./retrospectiveText";
import "./retrospective.css";

const MAX = 500;

/**
 * Batch 18 GRF-50 (B18-UX-SPECS §9; rulings R5 A and 7): one sprint's retrospective. The facts strip comes from the server (the
 * same sprint read as Reports); three lists — Went well, To improve, Actions — that any internal member adds to; the author,
 * the Capt. or the Chief Pilot change an entry (the server decides, `canEdit`); an Actions entry becomes a linked ACTION item.
 * Refreshes every 20 s while visible and on focus. Behind Features__SprintRetrospective (the route); partners never see it.
 */
export function SprintRetrospectivePage() {
  const { projectKey = "", sprintId: rawSprintId = "" } = useParams();
  const sprintId = Number(rawSprintId);
  const session = useSession();
  const retro = useRetrospective(sprintId, !session.isPartner);
  const announce = useAnnounce();
  const [notice, setNotice] = useState<string | null>(null);
  useTitle(retro.data ? t("title", { sprint: retro.data.sprint.name }) : t("retrospective"));

  if (session.isPartner) {
    return <PageNotAvailable title={t("notAvailable")} reason={t("notAvailableReason")} />;
  }
  if (retro.isPending) {
    return (
      <p className="gf-loading" role="status">
        {t("loading")}
      </p>
    );
  }
  if (retro.isError) return <LoadError error={retro.error} onRetry={() => void retro.refetch()} />;

  const data = retro.data;
  const sprint = data.sprint;
  const future = sprint.state === "future";
  const backlog = `/p/${encodeURIComponent(projectKey)}/backlog`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(retrospectiveAsText(data));
      setNotice(t("copied"));
      announce(t("copied"));
    } catch {
      setNotice(t("copyFailed"));
    }
  }

  return (
    <div className="gf-retro">
      <div className="gf-pagehead row">
        <div>
          <h1 lang={langFor(sprint.name)}>{t("title", { sprint: sprint.name })}</h1>
          <p className="sub">
            {sprint.startAt ? t("dates", { start: formatDate(sprint.startAt), end: formatDate(sprint.completedAt ?? sprint.endAt) }) : t("notStarted")}
            {" · "}
            <Link to={backlog}>{t("backToBacklog")}</Link>
          </p>
          {sprint.goal && (
            <p className="gf-retro-goal" lang={langFor(sprint.goal)}>
              {t("goal", { goal: sprint.goal })}
            </p>
          )}
        </div>
        <button type="button" className="btn sec" onClick={() => void copy()}>
          {t("copy")}
        </button>
      </div>

      {notice && (
        <div className="msg info gf-banner" role="status">
          <span>{notice}</span>
          <button type="button" className="linkish" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}
      {data.readOnly && (
        <p className="msg info" role="note">
          {t("readOnly")}
        </p>
      )}
      {!data.readOnly && data.readOnlyFrom && <p className="help">{t("readOnlyOn", { date: formatDate(data.readOnlyFrom) })}</p>}
      {future && (
        <p className="msg info" role="note">
          {t("futureSprint")}
        </p>
      )}

      <FactsStrip retro={data} />

      <div className="gf-retro-lists">
        {LISTS.map((list) => (
          <RetroList
            key={list.kind}
            kind={list.kind}
            title={list.title()}
            retro={data}
            projectKey={projectKey}
            canAdd={!data.readOnly && !future}
            onNotice={setNotice}
          />
        ))}
      </div>
    </div>
  );
}

function FactsStrip({ retro }: { retro: SprintRetrospective }) {
  const facts = retro.facts;
  if (!facts) return null;
  const total = facts.committedAtStart.items + facts.added.items;
  return (
    <section className="gf-retro-facts" aria-labelledby="retro-facts">
      <h2 id="retro-facts">{t("factsTitle")}</h2>
      <dl>
        {factRows(facts).map((row) => (
          <div key={row.label} className="gf-retro-fact">
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      {facts.itemsWithoutPoints > 0 && <p className="help">{t("withoutPoints", { count: facts.itemsWithoutPoints, total })}</p>}
    </section>
  );
}

function RetroList(props: {
  kind: RetrospectiveListKind;
  title: string;
  retro: SprintRetrospective;
  projectKey: string;
  canAdd: boolean;
  onNotice: (text: string) => void;
}) {
  const { kind, retro } = props;
  const queryClient = useQueryClient();
  const announce = useAnnounce();
  const action = useIdempotentAction();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const entries = entriesOf(retro, kind);
  const headingId = `retro-${kind}`;
  const fieldId = `retro-add-${kind}`;
  const add = useMutation({
    mutationFn: (value: string) => action.run((key) => addEntry(retro.sprint.id, key, kind, value), { kind, value }),
    onSuccess: async () => {
      setText("");
      setError(null);
      announce(`${t("add")}: ${props.title}`);
      await queryClient.invalidateQueries({ queryKey: retrospectiveKey(retro.sprint.id) });
    },
    onError: (failure) => setError(problemMessage(failure)),
  });

  function submit() {
    const value = text.trim();
    if (!value) return setError(t("needText"));
    if (value.length > MAX) return setError(t("tooLong"));
    add.mutate(value);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  }

  return (
    <section className="gf-retro-list" aria-labelledby={headingId}>
      <h2 id={headingId}>
        {props.title} <span className="gf-count">({entries.filter((entry) => entry.text !== null).length})</span>
      </h2>
      {entries.length === 0 && <p className="gf-empty">{t("empty")}</p>}
      <ul>
        {entries.map((entry) => (
          <EntryRow key={entry.id} entry={entry} retro={retro} projectKey={props.projectKey} onNotice={props.onNotice} />
        ))}
      </ul>
      {props.canAdd && (
        <div className="gf-form gf-retro-add">
          <label htmlFor={fieldId}>{t("addLabel", { list: props.title })}</label>
          <textarea
            id={fieldId}
            rows={2}
            maxLength={MAX}
            value={text}
            aria-describedby={`${fieldId}-hint${error ? ` ${fieldId}-error` : ""}`}
            aria-invalid={!!error || undefined}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
          />
          <p id={`${fieldId}-hint`} className="help">
            {t("addHint")} {text.length}/{MAX}
          </p>
          {error && (
            <p id={`${fieldId}-error`} className="err" role="alert">
              {error}
            </p>
          )}
          <button type="button" className="btn sec" disabled={add.isPending} onClick={submit}>
            {add.isPending ? t("adding") : t("add")}
          </button>
        </div>
      )}
    </section>
  );
}

function EntryRow({ entry, retro, projectKey, onNotice }: { entry: RetrospectiveEntry; retro: SprintRetrospective; projectKey: string; onNotice: (text: string) => void }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.text ?? "");
  const [conflict, setConflict] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [making, setMaking] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: retrospectiveKey(retro.sprint.id) });

  const save = useMutation({
    mutationFn: (text: string) => editEntry(retro.sprint.id, entry.id, entry.version, text),
    onSuccess: async () => {
      setEditing(false);
      setConflict(null);
      setError(null);
      await refresh();
    },
    onError: async (failure, text) => {
      if (isApiProblem(failure) && failure.status === 409 && failure.problem?.code === "version-conflict") {
        setConflict(text);
        setEditing(false);
        await refresh();
      } else setError(problemMessage(failure));
    },
  });
  const remove = useMutation({
    mutationFn: () => removeEntry(retro.sprint.id, entry.id, entry.version),
    onSuccess: refresh,
    onError: (failure) => onNotice(problemMessage(failure)),
  });

  if (entry.text === null) {
    return (
      <li className="gf-retro-entry removed">
        <p>{t("removedBy", { name: entry.removedBy?.displayName ?? "" })}</p>
        {entry.actionItem && <ActionChip entry={entry} />}
      </li>
    );
  }

  const author = entry.author;
  return (
    <li className="gf-retro-entry">
      <div className="gf-retro-who">
        <PersonAvatar name={author.displayName} avatarUrl={author.avatarUrl ?? null} isAgent={author.isAgent === true} className="gf-av-sm" />
        <span className="gf-retro-name">{author.displayName}</span>
        <time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt)}</time>
        {entry.edited && (
          <button type="button" className="linkish" aria-expanded={showHistory} onClick={() => setShowHistory((open) => !open)}>
            {t("edited")}
          </button>
        )}
      </div>
      {editing ? (
        <div className="gf-form">
          <label htmlFor={`retro-edit-${entry.id}`} className="vh">
            {t("edit")}
          </label>
          <textarea id={`retro-edit-${entry.id}`} rows={3} maxLength={MAX} value={draft} onChange={(event) => setDraft(event.target.value)} />
          {error && (
            <p className="err" role="alert">
              {error}
            </p>
          )}
          <div className="gf-retro-actions">
            <button type="button" className="btn" disabled={save.isPending} onClick={() => (draft.trim() ? save.mutate(draft.trim()) : setError(t("needText")))}>
              {t("save")}
            </button>
            <button type="button" className="btn sec" onClick={() => setEditing(false)}>
              {t("cancel")}
            </button>
          </div>
        </div>
      ) : (
        <p className="gf-retro-text" lang={langFor(entry.text)}>
          {entry.text}
        </p>
      )}
      {conflict !== null && (
        <div className="msg warn" role="alert">
          <p>{t("conflict")}</p>
          <label htmlFor={`retro-mine-${entry.id}`}>{t("yourText")}</label>
          <textarea id={`retro-mine-${entry.id}`} readOnly rows={2} value={conflict} />
          <button type="button" className="linkish" onClick={() => setConflict(null)}>
            Dismiss
          </button>
        </div>
      )}
      {showHistory && (
        <ul className="gf-retro-history" aria-label={t("previousText")}>
          {entry.revisions.map((revision) => (
            <li key={revision.editedAt}>
              <span lang={langFor(revision.text)}>{revision.text}</span>{" "}
              <span className="help">
                ({revision.editedBy.displayName}, {formatDateTime(revision.editedAt)})
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="gf-retro-actions">
        {entry.canEdit && !editing && (
          <>
            <button
              type="button"
              className="linkish"
              onClick={() => {
                setDraft(entry.text ?? "");
                setEditing(true);
              }}
            >
              {t("edit")}
            </button>
            <button type="button" className="linkish" disabled={remove.isPending} onClick={() => window.confirm(t("removeConfirm")) && remove.mutate()}>
              {t("remove")}
            </button>
          </>
        )}
        {entry.list === "actions" && !entry.actionItem && (
          <button type="button" className="btn sec" onClick={() => setMaking(true)}>
            {t("makeAction")}
          </button>
        )}
      </div>
      {entry.actionItem && <ActionChip entry={entry} />}
      {making && (
        <ActionItemDialog
          entry={entry}
          retro={retro}
          projectKey={projectKey}
          onClose={() => setMaking(false)}
          onDone={async () => {
            setMaking(false);
            await refresh();
          }}
        />
      )}
    </li>
  );
}

function ActionChip({ entry }: { entry: RetrospectiveEntry }) {
  const item = entry.actionItem;
  if (!item) return null;
  return (
    <p className="gf-retro-item">
      <Link className="gf-keylink" to={`/items/${encodeURIComponent(item.key)}`}>
        {item.key}
      </Link>{" "}
      <StatusBadge status={item.status} type="ACTION" />{" "}
      {item.owner && <span>{t("ownedBy", { name: item.owner.displayName })}</span>}
      {item.dueDate && <span> · {t("due", { date: formatDate(item.dueDate) })}</span>}
    </p>
  );
}

function ActionItemDialog(props: { entry: RetrospectiveEntry; retro: SprintRetrospective; projectKey: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { entry, retro } = props;
  const session = useSession();
  const action = useIdempotentAction();
  const members = useProjectMembers(props.projectKey);
  const directory = useUserDirectory();
  const [summary, setSummary] = useState(entry.text ?? "");
  const [ownerId, setOwnerId] = useState("");
  const [dueDate, setDueDate] = useState(defaultDueDate(retro.sprint.endAt));
  const [errors, setErrors] = useState<{ summary?: string; owner?: string; form?: string }>({});
  // Batch 19 (OBS-B18U-7): only members who can act own an action (the server's RetrospectiveRules.MayOwnAction).
  const people = (members.data ?? [])
    .filter((member) => mayOwnAction(member.roles))
    .map((member) => directory.byId.get(member.userId))
    .filter((user): user is NonNullable<typeof user> => !!user && user.userKind !== "partner")
    .sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
  const create = useMutation({
    mutationFn: () =>
      action.run((key) => createActionItem(retro.sprint.id, entry.id, key, { summary: summary.trim(), ownerId: Number(ownerId), dueDate: dueDate || null }), {
        summary,
        ownerId,
        dueDate,
      }),
    onSuccess: props.onDone,
    onError: (failure) => {
      const fields = isApiProblem(failure) ? (failure.problem?.errors ?? []) : [];
      const field = (name: string) => fields.find((error) => error.field === name)?.message;
      const summaryError = field("summary");
      const ownerError = field("ownerId");
      // D-B18-02: a refusal the server names on a field (the §6 summary rule) shows on that field only, not again above the form.
      setErrors({ summary: summaryError, owner: ownerError, form: summaryError || ownerError ? undefined : problemMessage(failure) });
    },
  });
  return (
    <Dialog
      open
      title={t("actionTitle")}
      onClose={props.onClose}
      busy={create.isPending}
      footer={
        <>
          <button type="button" className="btn sec" onClick={props.onClose}>
            {t("cancel")}
          </button>
          <button
            type="button"
            className="btn"
            disabled={create.isPending}
            onClick={() => {
              // D-B18-02 (UX §9.6): the action item is a work item, so the §6 summary rule applies here too, at once, while
              // Features__SummaryValidation is on (the server checks it again).
              const quality = session.flag("SummaryValidation") ? textQualityErrors({ summary, description: "" }).summary : undefined;
              const next = { summary: summary.trim() ? quality : t("needSummary"), owner: ownerId ? undefined : t("needOwner") };
              setErrors(next);
              if (!next.summary && !next.owner) create.mutate();
            }}
          >
            {create.isPending ? t("creating") : t("create")}
          </button>
        </>
      }
    >
      <div className="gf-form stack">
        {errors.form && (
          <div className="msg" role="alert">
            {errors.form}
          </div>
        )}
        <div className="gf-field">
          <label htmlFor="retro-action-summary">
            {t("summary")}
            <span className="req"> (required)</span>
          </label>
          <input
            id="retro-action-summary"
            value={summary}
            aria-invalid={!!errors.summary || undefined}
            aria-describedby={errors.summary ? "retro-action-summary-error" : undefined}
            onChange={(event) => setSummary(event.target.value)}
          />
          {errors.summary && (
            <p className="err" id="retro-action-summary-error" role="alert">
              {errors.summary}
            </p>
          )}
        </div>
        <div className="gf-field">
          <label htmlFor="retro-action-owner">
            {t("owner")}
            <span className="req"> (required)</span>
          </label>
          <select
            id="retro-action-owner"
            value={ownerId}
            aria-invalid={!!errors.owner || undefined}
            aria-describedby={errors.owner ? "retro-action-owner-error" : undefined}
            onChange={(event) => setOwnerId(event.target.value)}
          >
            <option value="">{t("chooseOwner")}</option>
            {people.map((user) => (
              <option key={user.id} value={String(user.id)}>
                {userLabel(user)}
              </option>
            ))}
          </select>
          {errors.owner && (
            <p className="err" id="retro-action-owner-error" role="alert">
              {errors.owner}
            </p>
          )}
        </div>
        <div className="gf-field">
          <label htmlFor="retro-action-due">{t("dueDate")}</label>
          <input id="retro-action-due" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
        </div>
      </div>
    </Dialog>
  );
}
